// components/social/ImportModal.jsx
//
// Custom page → Import: reads the posts of a connected account (the last one authenticated is
// preselected) and creates a Custom profile for every cake that is not on Home or in Custom yet.
// The worker compares the photos (perceptual hash), so re-posted / re-cropped copies are skipped.
// Fields come from the caption + photo via OneAdmin AI; anything it cannot tell gets the first
// valid option and the profile is marked "Review".
import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { getFirebase } from '../../services/firebase';
import { createPortal } from 'react-dom';
import { FaTimes, FaFileImport, FaPlug } from 'react-icons/fa';
import { useSocial } from './SocialProvider';
import { PLATFORMS, platformMeta, watchTask } from '../../services/socialService';
import './social.css';

export default function ImportModal({ onClose }) {
  const social = useSocial();
  const [platform, setPlatform] = useState(social.lastImportPlatform);
  const [limit, setLimit] = useState(30);
  const [allPosts, setAllPosts] = useState(true);
  const [taskId, setTaskId] = useState(null);
  const [task, setTask] = useState(null);

  useEffect(() => (taskId ? watchTask(taskId, setTask) : undefined), [taskId]);

  // Imported cakes deleted in Custom: the import skips them. Shown so it is never a mystery why.
  const [deleted, setDeleted] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const loadDeleted = async () => {
    const uid = getFirebase().auth?.currentUser?.uid;
    if (!uid) return;
    try {
      const snap = await getDocs(query(collection(getFirebase().db, 'socialImportSkip'), where('uid', '==', uid)));
      setDeleted(snap.docs.map((d) => ({ ref: d.ref, ...d.data() })));
    } catch { setDeleted([]); }
  };
  useEffect(() => { void loadDeleted(); }, []);
  // Only cakes deleted as "not wanted" are skipped; import-issue deletions (and older records
  // made before the choice existed) are imported again.
  const deletedHere = deleted.filter((d) => d.platform === platform && d.kind === 'deleted');
  const reimportHere = deleted.filter((d) => d.platform === platform && d.kind === 'reimport');
  const allowAgain = async (items) => {
    await Promise.all(items.map((d) => deleteDoc(d.ref).catch(() => undefined)));
    await loadDeleted();
  };

  const acc = social.accounts[platform];
  const meta = platformMeta(platform);
  const running = task && ['queued', 'running', 'live'].includes(task.status);
  const result = (task?.status === 'done' || (task?.status === 'cancelled' && task.result)) ? task.result : null;
  const [stopping, setStopping] = useState(false);

  const start = async () => {
    const want = allPosts ? 'all' : limit;
    const id = acc?.connected
      ? await social.importPosts(platform, want)
      : await social.connect(platform, async () => setTaskId(await social.importPosts(platform, want)));
    if (acc?.connected) setTaskId(id);
  };

  return createPortal(
    <div className="social-modal-overlay" onClick={running ? undefined : onClose}>
      <div className="social-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Import from social media">
        <div className="social-modal-head">
          <strong><FaFileImport /> Import cakes from social media</strong>
          <button type="button" className="social-btn social-btn--quiet" onClick={onClose} aria-label="Close"><FaTimes /></button>
        </div>

        {!task && (
          <>
            <p className="social-muted">New cakes are added to Custom; ones already in PCake are skipped.</p>
            <div className="social-platforms">
              {PLATFORMS.map((p) => {
                const a = social.accounts[p.id];
                return (
                  <div key={p.id} className={`social-platform${platform === p.id ? ' is-selected' : ''}`} onClick={() => setPlatform(p.id)} role="radio" aria-checked={platform === p.id}>
                    <span className="social-live-badge" style={{ background: p.color }}>{p.short}</span>
                    <div className="social-platform-text">
                      <strong>{p.label}{social.lastImportPlatform === p.id ? ' · last used' : ''}</strong>
                      <span>{a?.connected ? (a.accountName || 'Connected') : 'Not connected — you log in once'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <label className="social-field">
              <span>All posts</span>
              <input type="checkbox" checked={allPosts} onChange={(e) => setAllPosts(e.target.checked)} style={{ width: 18, height: 18 }} />
            </label>
            {!allPosts && (
              <label className="social-field">
                <span>Newest posts to check</span>
                <input type="number" min={1} max={5000} value={limit} onChange={(e) => setLimit(Math.max(1, Math.min(5000, Number(e.target.value) || 1)))} />
              </label>
            )}
            {reimportHere.length > 0 && (
              <div className="social-muted" style={{ marginTop: 8 }}>
                {reimportHere.length} cake{reimportHere.length === 1 ? '' : 's'} deleted as an import issue will be imported again.
              </div>
            )}
            {deletedHere.length > 0 && (
              <div className="social-muted" style={{ marginTop: 8 }}>
                {deletedHere.length} cake{deletedHere.length === 1 ? '' : 's'} you deleted after importing will be skipped.{' '}
                <button type="button" className="social-btn social-btn--quiet" style={{ height: 'auto', padding: 0, color: '#fa4848' }} onClick={() => setShowDeleted((v) => !v)}>
                  {showDeleted ? 'Hide' : 'Show'}
                </button>
                {showDeleted && (
                  <ul className="social-list">
                    {deletedHere.map((d) => (
                      <li key={d.ref.id}>
                        {d.theme || 'Untitled'}
                        {d.postUrl && <a href={d.postUrl} target="_blank" rel="noreferrer">post</a>}
                        <button type="button" className="social-btn social-btn--quiet" style={{ height: 'auto', padding: '0 4px', color: '#fa4848' }} onClick={() => allowAgain([d])}>import again</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {!social.worker.online && <div className="social-note social-note--warn">The social worker is offline; the import starts when it is back.</div>}
            <div className="social-modal-foot">
              <button type="button" className="social-btn" onClick={onClose}>Cancel</button>
              <button type="button" className="social-btn social-btn--primary" onClick={start}>
                {acc?.connected ? <><FaFileImport /> Import from {meta?.label}</> : <><FaPlug /> Connect {meta?.label} & import</>}
              </button>
            </div>
          </>
        )}

        {task && running && (
          <div className="social-progress">
            <span className="social-spinner" />
            <div>
              <strong>{task.status === 'queued' ? 'Waiting for the social worker…' : task.message}</strong>
              {task.progress?.total > 0 && (
                <>
                  <progress max={task.progress.total} value={task.progress.done} />
                  <span className="social-muted">{task.progress.created} new so far</span>
                </>
              )}
              {task.status === 'live' && task.sessionId && (
                <button type="button" className="social-btn social-btn--primary" onClick={() => social.openLive(task.sessionId)}>Open the login view</button>
              )}
              <button
                type="button"
                className="social-btn"
                style={{ alignSelf: 'flex-start' }}
                disabled={stopping}
                onClick={async () => { setStopping(true); try { await social.cancelTask(task.id); } catch { setStopping(false); } }}
              >
                {stopping ? 'Stopping…' : 'Cancel import'}
              </button>
            </div>
          </div>
        )}

        {result && (
          <div className="social-result">
            {task?.status === 'cancelled' && <div className="social-note social-note--warn">Cancelled after {result.checked ?? 0} of {result.found} posts.</div>}
            <p><strong>{result.createdCount}</strong> new cake profile(s) created from {result.found} post(s); {result.skippedCount} skipped.</p>
            {result.created?.length > 0 && (
              <ul className="social-list">
                {result.created.map((c) => (
                  <li key={c.id}>
                    {c.theme || 'Untitled'}
                    {c.defaults?.length > 0 && <span className="social-chip">Review: {c.defaults.join(', ')}</span>}
                    {c.postUrl && <a href={c.postUrl} target="_blank" rel="noreferrer">post</a>}
                  </li>
                ))}
              </ul>
            )}
            {result.skipped?.length > 0 && (
              <details>
                <summary>Skipped</summary>
                <ul className="social-list social-muted">
                  {result.skipped.map((s, i) => <li key={i}>{s.reason}{s.postUrl && <> · <a href={s.postUrl} target="_blank" rel="noreferrer">post</a></>}</li>)}
                </ul>
              </details>
            )}
            <div className="social-modal-foot"><button type="button" className="social-btn social-btn--primary" onClick={onClose}>Close</button></div>
          </div>
        )}

        {task && (task.status === 'failed' || (task.status === 'cancelled' && !task.result)) && (
          <div className="social-result">
            <div className="social-note social-note--warn">{task.error || task.message || 'The import did not finish.'}</div>
            <div className="social-modal-foot">
              <button type="button" className="social-btn" onClick={() => { setTask(null); setTaskId(null); }}>Try again</button>
              <button type="button" className="social-btn social-btn--primary" onClick={onClose}>Close</button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.getElementById('portal-root') || document.body,
  );
}
